/**
 * Fitness plan domain vocabulary (product language).
 *
 * | Domain              | Firestore / callable wire      |
 * |---------------------|--------------------------------|
 * | Program             | `blocks[]` item                |
 * | programId           | `blockId`                      |
 * | activeProgramId     | `activeBlockId`                |
 * | split (assignment)  | `schedule`                     |
 * | splitPattern        | (planner-only; not persisted)  |
 *
 * Feature code uses domain names. Only persistence adapters in this file
 * read or write legacy wire field names via `WIRE_*` constants.
 */

import {
  FITNESS_SCHEDULE_KEYS,
  type FitnessPlanGoal,
  type FitnessPlanWorkout,
  type FitnessScheduleKey,
} from './fitness-plan.types';

/** Legacy Firestore / callable JSON keys — persistence boundary only. */
export const WIRE_PROGRAMS = 'blocks' as const;
export const WIRE_PROGRAM_ID = 'blockId' as const;
export const WIRE_ACTIVE_PROGRAM_ID = 'activeBlockId' as const;
export const WIRE_SCHEDULE = 'schedule' as const;

/** Mon–Sun workout assignment (UI: Split). */
export type FitnessPlanSplit = Record<FitnessScheduleKey, string | null>;

export type FitnessPlanProgramGenerationStatus =
  | 'ready'
  | 'processing'
  | 'error';

export type ProgramGenerationStep =
  | 'goal_profile'
  | 'training_strategy'
  | 'program_phase'
  | 'workout_blueprints'
  | 'weekly_schedule_split'
  | 'movement_slot_plan'
  | 'exercise_selection'
  | 'final_program_review'
  | 'schema_validation'
  | 'finalising'
  /** @deprecated legacy persisted step ids */
  | 'weekly_split'
  | 'exercise_allocation'
  | 'validation';

export type ProgramGenerationRunStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed';

export interface ProgramGenerationProgress {
  status: ProgramGenerationRunStatus;
  currentStep: ProgramGenerationStep;
  progressPercent: number;
  message: string;
  startedAt?: string;
  updatedAt?: string;
  completedAt?: string;
  error?: string;
}

export interface ProgramGoalProfileSelection {
  selectionId?: string;
  displayPath?: string;
  scope?: 'program_wide' | 'distributed';
  category?: string;
  subcategory?: string;
  focusTag?: string;
}

export interface ProgramGoalProfile {
  primary?: string;
  primaryDisplay?: string;
  interpretation?: string;
  selections?: ProgramGoalProfileSelection[];
  programWideRules?: string[];
  distributedFocuses?: string[];
  equipmentConstraints?: string[];
  sessionConstraints?: string[];
}

export interface ProgramTrainingStrategy {
  prioritizedMuscles?: string[];
  maintainedMuscles?: string[];
  deemphasizedMuscles?: string[];
  weeklyFrequency?: number;
  weeklyVolumeTargets?: { muscle: string; targetSets: number }[];
  trainingStyle?: string;
  equipmentConstraints?: string;
  rationale?: string;
}

export interface ProgramPhaseArtifact {
  name?: string;
  durationWeeks?: number;
  focus?: string;
  progressionStyle?: string;
  aggressiveness?: string;
  successMetrics?: string[];
  rationale?: string;
}

export interface ProgramWeeklyScheduleSplit {
  splitPattern?: string;
  trainingDays?: number;
  scheduledSessions?: { day: string; workoutId: string; blueprintId?: string }[];
  restDays?: string[];
  orderingRationale?: string;
  recoveryRationale?: string;
  rationale?: string;
}

export interface ProgramMovementSlot {
  slotId?: string;
  blueprintId?: string;
  movementPattern?: string;
  movementIntention?: string;
  targetMuscle?: string;
  priority?: string;
  setsIntent?: string;
  repsIntent?: string;
  restIntent?: string;
  rationale?: string;
}

export interface ProgramExerciseSelectionItem {
  slotId?: string;
  exerciseName?: string;
  primaryTarget?: string;
  secondaryTargets?: string[];
  sets?: number;
  reps?: string;
  rest?: string;
  tempo?: string;
  notes?: string;
  substitutions?: string[];
  rationale?: string;
}

export interface ProgramSchemaValidationResult {
  passed?: boolean;
  errors?: string[];
  failedArtifact?: ProgramGenerationStep;
}

export interface ProgramWeeklySplitDesign {
  splitPattern?: string;
  trainingDays?: number;
  sessionTemplates?: { name: string; emphasizedMuscles: string[] }[];
  rationale?: string;
}

export interface ProgramWorkoutBlueprint {
  blueprintId?: string;
  name?: string;
  focus?: string;
  purpose?: string;
  targetAdaptations?: string;
  emphasizedAreas?: string[];
  deemphasizedAreas?: string[];
  targetMuscles?: string[];
  movementIntentions?: string[];
  setRepStyle?: string;
  fatigueNotes?: string;
  strategyRelationship?: string;
  targetMuscleVolumes?: {
    muscle: string;
    targetSets: number;
    intensity?: string;
  }[];
  addressesSelectionIds?: string[];
  recoveryNotes?: string;
  rationale?: string;
}

export interface ProgramExerciseAllocation {
  blueprintId?: string;
  workoutName?: string;
  muscleAllocations?: {
    muscle: string;
    targetSets?: number;
    movementPatterns?: string[];
    exerciseHints?: string[];
    exercises?: { exerciseName: string; sets: number }[];
  }[];
}

export interface ProgramBuildAttemptStep {
  step: ProgramGenerationStep;
  status: 'pending' | 'active' | 'complete' | 'failed';
}

export interface ProgramBuildAttempt {
  attemptNumber: number;
  trigger?: 'initial' | 'validation_repair';
  outcome?: 'running' | 'failed' | 'completed';
  failedStep?: ProgramGenerationStep;
  error?: string;
  fromStep?: ProgramGenerationStep;
  steps: ProgramBuildAttemptStep[];
}

export interface ProgramValidationReport {
  passed?: boolean;
  retryable?: boolean;
  goalAlignment?: string[];
  volumeBalance?: string[];
  warnings?: string[];
  errors?: string[];
}

/** One training program: workouts plus weekly split assignment. */
export interface FitnessPlanProgram {
  programId: string;
  name: string;
  /** Optional phase notes (e.g. mesocycle intent). */
  description?: string;
  order: number;
  workouts: FitnessPlanWorkout[];
  split: FitnessPlanSplit;
  generationStatus?: FitnessPlanProgramGenerationStatus;
  generationJobId?: string;
  generationError?: string;
  generation?: ProgramGenerationProgress;
  goalProfile?: ProgramGoalProfile;
  trainingStrategy?: ProgramTrainingStrategy;
  phase?: ProgramPhaseArtifact;
  weeklySplit?: ProgramWeeklySplitDesign;
  weeklyScheduleSplit?: ProgramWeeklyScheduleSplit;
  workoutBlueprints?: ProgramWorkoutBlueprint[];
  exerciseAllocations?: ProgramExerciseAllocation[];
  movementSlotPlan?: { slots?: ProgramMovementSlot[] };
  exerciseSelections?: ProgramExerciseSelectionItem[];
  finalProgram?: Record<string, unknown>;
  validationReport?: ProgramValidationReport;
  schemaValidationResult?: ProgramSchemaValidationResult;
  generationLogs?: Record<string, unknown>[];
  retryHistory?: Record<string, unknown>[];
  generationMetadata?: Record<string, unknown>;
  buildAttempts?: ProgramBuildAttempt[];
}

export interface FitnessPlanDetail {
  planId: string;
  name: string;
  goal?: FitnessPlanGoal;
  notes: string;
  programs: FitnessPlanProgram[];
  activeProgramId: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Wire shape for one program row in `get_fitness_plan` / `save_fitness_plan`. */
export interface StoredProgramRowWire {
  [WIRE_PROGRAM_ID]: string;
  name: string;
  description?: string;
  order: number;
  workouts: FitnessPlanWorkout[];
  [WIRE_SCHEDULE]: Record<string, string | null>;
  generationStatus?: FitnessPlanProgramGenerationStatus;
  generationJobId?: string;
  generationError?: string;
  generation?: ProgramGenerationProgress;
  goalProfile?: ProgramGoalProfile;
  trainingStrategy?: ProgramTrainingStrategy;
  phase?: ProgramPhaseArtifact;
  weeklySplit?: ProgramWeeklySplitDesign;
  weeklyScheduleSplit?: ProgramWeeklyScheduleSplit;
  workoutBlueprints?: ProgramWorkoutBlueprint[];
  exerciseAllocations?: ProgramExerciseAllocation[];
  movementSlotPlan?: { slots?: ProgramMovementSlot[] };
  exerciseSelections?: ProgramExerciseSelectionItem[];
  finalProgram?: Record<string, unknown>;
  validationReport?: ProgramValidationReport;
  schemaValidationResult?: ProgramSchemaValidationResult;
  generationLogs?: Record<string, unknown>[];
  retryHistory?: Record<string, unknown>[];
  generationMetadata?: Record<string, unknown>;
  buildAttempts?: ProgramBuildAttempt[];
}

export interface StoredFitnessPlanDetailWire {
  planId: string;
  name: string;
  goal?: FitnessPlanGoal;
  notes: string;
  [WIRE_PROGRAMS]?: StoredProgramRowWire[];
  [WIRE_ACTIVE_PROGRAM_ID]?: string | null;
  workouts?: FitnessPlanWorkout[];
  [WIRE_SCHEDULE]?: Record<string, string | null>;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PlanSavePayload {
  planId?: string;
  name: string;
  goal?: FitnessPlanGoal;
  notes?: string;
  programs: FitnessPlanProgram[];
  activeProgramId: string | null;
}

export interface StoredPlanSavePayload {
  planId?: string;
  name: string;
  goal?: FitnessPlanGoal;
  notes?: string;
  [WIRE_PROGRAMS]: StoredProgramRowWire[];
  [WIRE_ACTIVE_PROGRAM_ID]: string | null;
}

export function emptySplit(): FitnessPlanSplit {
  const o = {} as FitnessPlanSplit;
  for (const k of FITNESS_SCHEDULE_KEYS) {
    o[k] = null;
  }
  return o;
}

export function cloneSplit(
  split: FitnessPlanSplit | undefined | null,
): FitnessPlanSplit {
  const out = emptySplit();
  if (!split) {
    return out;
  }
  for (const k of FITNESS_SCHEDULE_KEYS) {
    const v = split[k];
    out[k] = typeof v === 'string' && v.trim() ? v.trim() : null;
  }
  return out;
}

const _GEN_ARTIFACT_KEYS = [
  'generation',
  'goalProfile',
  'trainingStrategy',
  'phase',
  'weeklySplit',
  'weeklyScheduleSplit',
  'workoutBlueprints',
  'exerciseAllocations',
  'movementSlotPlan',
  'exerciseSelections',
  'finalProgram',
  'validationReport',
  'schemaValidationResult',
  'generationLogs',
  'retryHistory',
  'generationMetadata',
  'buildAttempts',
] as const;

function copyProgramGenerationArtifacts<
  T extends FitnessPlanProgram | StoredProgramRowWire,
  S extends FitnessPlanProgram | StoredProgramRowWire,
>(target: T, source: S): T {
  for (const key of _GEN_ARTIFACT_KEYS) {
    const val = source[key as keyof S];
    if (val !== undefined && val !== null) {
      (target as unknown as Record<string, unknown>)[key] =
        typeof val === 'object'
          ? (JSON.parse(JSON.stringify(val)) as unknown)
          : val;
    }
  }
  return target;
}

export function programFromStored(
  wire: StoredProgramRowWire,
): FitnessPlanProgram {
  const program: FitnessPlanProgram = {
    programId: String(wire[WIRE_PROGRAM_ID] ?? '').trim(),
    name: wire.name,
    order: wire.order,
    workouts: wire.workouts ?? [],
    split: cloneSplit(wire[WIRE_SCHEDULE] as FitnessPlanSplit),
  };
  const desc =
    typeof wire.description === 'string' ? wire.description.trim() : '';
  if (desc) {
    program.description = desc;
  }
  if (
    wire.generationStatus === 'ready' ||
    wire.generationStatus === 'processing' ||
    wire.generationStatus === 'error'
  ) {
    program.generationStatus = wire.generationStatus;
  }
  const jobId =
    typeof wire.generationJobId === 'string' ? wire.generationJobId.trim() : '';
  if (jobId) {
    program.generationJobId = jobId;
  }
  const genErr =
    typeof wire.generationError === 'string' ? wire.generationError.trim() : '';
  if (genErr) {
    program.generationError = genErr;
  }
  copyProgramGenerationArtifacts(program, wire);
  return program;
}

export function programToStored(
  program: FitnessPlanProgram,
): StoredProgramRowWire {
  const wire: StoredProgramRowWire = {
    [WIRE_PROGRAM_ID]: program.programId,
    name: program.name,
    order: program.order,
    workouts: program.workouts,
    [WIRE_SCHEDULE]: cloneSplit(program.split),
  };
  const desc =
    typeof program.description === 'string' ? program.description.trim() : '';
  if (desc) {
    wire.description = desc;
  }
  if (
    program.generationStatus === 'ready' ||
    program.generationStatus === 'processing' ||
    program.generationStatus === 'error'
  ) {
    wire.generationStatus = program.generationStatus;
  }
  const jobId =
    typeof program.generationJobId === 'string'
      ? program.generationJobId.trim()
      : '';
  if (jobId) {
    wire.generationJobId = jobId;
  }
  const genErr =
    typeof program.generationError === 'string'
      ? program.generationError.trim()
      : '';
  if (genErr) {
    wire.generationError = genErr;
  }
  copyProgramGenerationArtifacts(wire, program);
  return wire;
}

export function clonePrograms(
  programs: FitnessPlanProgram[] | undefined | null,
): FitnessPlanProgram[] {
  if (!Array.isArray(programs)) {
    return [];
  }
  return programs.map((p) => {
    const program: FitnessPlanProgram = {
      programId: p.programId,
      name: p.name,
      order: p.order,
      workouts: JSON.parse(
        JSON.stringify(p.workouts ?? []),
      ) as FitnessPlanWorkout[],
      split: cloneSplit(p.split),
    };
    const desc = typeof p.description === 'string' ? p.description.trim() : '';
    if (desc) {
      program.description = desc;
    }
    if (
      p.generationStatus === 'ready' ||
      p.generationStatus === 'processing' ||
      p.generationStatus === 'error'
    ) {
      program.generationStatus = p.generationStatus;
    }
    const jobId =
      typeof p.generationJobId === 'string' ? p.generationJobId.trim() : '';
    if (jobId) {
      program.generationJobId = jobId;
    }
    const genErr =
      typeof p.generationError === 'string' ? p.generationError.trim() : '';
    if (genErr) {
      program.generationError = genErr;
    }
    copyProgramGenerationArtifacts(program, p);
    return program;
  });
}

export function legacyProgramIdForPlan(planId: string): string {
  return `legacy-${planId.trim()}`;
}

/** Map `get_fitness_plan` wire payload to domain (active programs only; server filters soft-deleted). */
export function planDetailFromStored(
  raw: StoredFitnessPlanDetailWire,
): FitnessPlanDetail {
  const base: FitnessPlanDetail = {
    planId: raw.planId,
    name: raw.name,
    goal: raw.goal,
    notes: raw.notes ?? '',
    programs: [],
    activeProgramId: null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };

  const programRows = raw[WIRE_PROGRAMS];
  if (Array.isArray(programRows) && programRows.length > 0) {
    const programs = clonePrograms(programRows.map(programFromStored));
    let activeProgramId = raw[WIRE_ACTIVE_PROGRAM_ID] ?? null;
    if (
      activeProgramId &&
      !programs.some((p) => p.programId === activeProgramId)
    ) {
      activeProgramId = programs[0]?.programId ?? null;
    } else if (!activeProgramId && programs.length > 0) {
      activeProgramId = programs[0].programId;
    }
    return { ...base, programs, activeProgramId };
  }

  const workouts = Array.isArray(raw.workouts) ? raw.workouts : [];
  const split = cloneSplit(raw[WIRE_SCHEDULE] as FitnessPlanSplit);
  const hasLegacy =
    workouts.length > 0 ||
    FITNESS_SCHEDULE_KEYS.some((k) => {
      const v = split[k];
      return typeof v === 'string' && v.trim().length > 0;
    });

  if (hasLegacy) {
    const programId = legacyProgramIdForPlan(raw.planId);
    return {
      ...base,
      programs: [
        {
          programId,
          name: 'Program 1',
          order: 0,
          workouts: JSON.parse(
            JSON.stringify(workouts),
          ) as FitnessPlanWorkout[],
          split,
        },
      ],
      activeProgramId: programId,
    };
  }

  return base;
}

export function planSavePayloadToStored(
  payload: PlanSavePayload,
): StoredPlanSavePayload {
  return {
    ...(payload.planId ? { planId: payload.planId } : {}),
    name: payload.name,
    goal: payload.goal,
    notes: payload.notes,
    [WIRE_PROGRAMS]: clonePrograms(payload.programs).map(programToStored),
    [WIRE_ACTIVE_PROGRAM_ID]: payload.activeProgramId,
  };
}

export function sessionProgramIdFromStored(
  wire: { [WIRE_PROGRAM_ID]?: unknown } | null | undefined,
): string {
  const raw = wire?.[WIRE_PROGRAM_ID];
  return typeof raw === 'string' ? raw.trim() : '';
}

export function sessionProgramIdToStored(
  programId: string,
): Record<typeof WIRE_PROGRAM_ID, string> {
  return { [WIRE_PROGRAM_ID]: programId.trim() };
}

export interface StoredFitnessPlanSessionSummaryWire {
  sessionId: string;
  [WIRE_PROGRAM_ID]: string;
  workoutId: string;
  workoutName: string;
  status: import('./fitness-plan.types').FitnessPlanSessionStatus;
  startedAt: string;
  finishedAt?: string | null;
}

export interface FitnessPlanSessionSummary {
  sessionId: string;
  programId: string;
  workoutId: string;
  workoutName: string;
  status: import('./fitness-plan.types').FitnessPlanSessionStatus;
  startedAt: string;
  finishedAt?: string | null;
}

export function sessionSummaryFromStored(
  wire: StoredFitnessPlanSessionSummaryWire,
): FitnessPlanSessionSummary {
  return {
    sessionId: wire.sessionId,
    programId: sessionProgramIdFromStored(wire),
    workoutId: wire.workoutId,
    workoutName: wire.workoutName,
    status: wire.status,
    startedAt: wire.startedAt,
    finishedAt: wire.finishedAt,
  };
}

export interface StoredFitnessPlanSessionWire extends StoredFitnessPlanSessionSummaryWire {
  exercises: unknown[];
  finishedAt?: string | null;
  coachName?: string;
  coachId?: string;
  coachAvatarStoragePath?: string;
  activeRest?: unknown;
  customEncouragementCheers?: unknown[];
  encouragementCheerPreferences?: unknown;
  encouragementCheerScope?:
    | 'look_only'
    | 'form_only'
    | 'both'
    | 'exercise_only';
  encouragementCheerProfanityEnabled?: boolean;
  coachSettings?: unknown;
  aiAnalysis?: unknown;
  aiAnalysisAt?: string | null;
}

export function sessionFromStored(wire: StoredFitnessPlanSessionWire) {
  const programId = sessionProgramIdFromStored(wire);
  const { [WIRE_PROGRAM_ID]: _omit, ...rest } = wire;
  return {
    ...rest,
    programId,
  };
}
